/**
 * Dragging a camera from a list panel onto the view.
 *
 * The browser's own drag-and-drop crosses panel boundaries for free, which is
 * why the layout grid used it - but it is mouse-only, and every other gesture in
 * this app went to pointer events for exactly that reason. So this is the same
 * idea rebuilt on pointer capture: a module store holds the payload while a drag
 * is live, surfaces register the rectangles they accept, and the store hit-tests
 * the pointer against them on every move.
 *
 * Targets are hit-tested last-registered-first, so a surface mounted over
 * another one wins. `hitTest` is pure and takes the rectangles it is given, so
 * the routing is testable without a DOM.
 */

import type { Unsubscribe } from '@/types/common';
import type { CameraDragPayload } from '@/types/panels';

/** Viewport coordinates of the pointer driving the drag. */
interface DragPoint {
    x: number;
    y: number;
}

/** The edges the hit test reads; a DOMRect satisfies it. */
interface DragRect {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

/**
 * A surface that accepts camera drops. `getRect` answers its current viewport
 * rectangle, so a panel that moves needs no re-registration.
 */
interface DropTarget {
    id: string;
    getRect?: () => DragRect | null;
    onEnter?: (point: DragPoint) => void;
    onMove?: (point: DragPoint) => void;
    onLeave?: () => void;
    onDrop?: (payload: CameraDragPayload, point: DragPoint | null) => void;
}

interface DragSnapshot {
    payload: CameraDragPayload | null;
    point: DragPoint | null;
    /** Id of the target under the pointer, for surfaces that highlight. */
    overText: string | null;
}

const idleDragSnapshot: DragSnapshot = { payload: null, point: null, overText: null };

let snapshot: DragSnapshot = idleDragSnapshot;
const listeners = new Set<() => void>();
/** Registered surfaces, in registration order; the last match wins. */
const targets: DropTarget[] = [];
let enteredDropTarget: DropTarget | null = null;

function emit(nextDragSnapshot: DragSnapshot): void {
    snapshot = nextDragSnapshot;
    for (const fn of listeners) {
        try {
            fn();
        } catch { /* a listener throwing must not stop the rest */ }
    }
}

const isInside = (rect: DragRect | null, point: DragPoint): boolean =>
    !!rect && point.x >= rect.left && point.x <= rect.right &&
    point.y >= rect.top && point.y <= rect.bottom;

/** The topmost registered target under the point, or null. */
export function hitTest(
    list: readonly DropTarget[], point: DragPoint | null,
): DropTarget | null {
    if (!point) {
        return null;
    }
    for (let i = list.length - 1; i >= 0; --i) {
        const target = list[i];
        if (target !== undefined && isInside(target.getRect?.() ?? null, point)) {
            return target;
        }
    }
    return null;
}

function route(point: DragPoint): DropTarget | null {
    const nextDropTarget = hitTest(targets, point);
    if (nextDropTarget !== enteredDropTarget) {
        enteredDropTarget?.onLeave?.();
        enteredDropTarget = nextDropTarget;
        enteredDropTarget?.onEnter?.(point);
    }
    enteredDropTarget?.onMove?.(point);
    return nextDropTarget;
}

/** Begin a drag. `payload` is `{kind:'camera', deviceId, name, t?}`. */
export function startDrag(payload: CameraDragPayload, point: DragPoint): void {
    enteredDropTarget = null;
    const overDropTarget = route(point);
    emit({ payload, point, overText: overDropTarget?.id ?? null });
}

export function moveDrag(point: DragPoint): void {
    if (!snapshot.payload) {
        return;
    }
    const overDropTarget = route(point);
    emit({ ...snapshot, point, overText: overDropTarget?.id ?? null });
}

/** Release: the target under the pointer, if any, takes the payload. */
export function endDrag(): boolean {
    const { payload, point } = snapshot;
    const target = enteredDropTarget;
    enteredDropTarget = null;
    emit(idleDragSnapshot);
    if (!payload || !target) {
        target?.onLeave?.();
        return false;
    }
    target.onLeave?.();
    target.onDrop?.(payload, point);
    return true;
}

export function cancelDrag(): void {
    const target = enteredDropTarget;
    enteredDropTarget = null;
    emit(idleDragSnapshot);
    target?.onLeave?.();
}

/**
 * Register a surface that accepts camera drops. `getRect` returns its current
 * viewport rectangle (`getBoundingClientRect`), so a moving panel needs no
 * re-registration.
 */
export function registerDropTarget(target: DropTarget): Unsubscribe {
    targets.push(target);
    return () => {
        const i = targets.indexOf(target);
        if (i >= 0) {
            targets.splice(i, 1);
        }
        if (enteredDropTarget === target) {
            enteredDropTarget = null;
        }
    };
}

export const subscribe = (fn: () => void): Unsubscribe => {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
};

export const getSnapshot = (): DragSnapshot => snapshot;

export function __resetForTest(): void {
    listeners.clear();
    targets.length = 0;
    enteredDropTarget = null;
    snapshot = idleDragSnapshot;
}
